'use client'

import { SmartHeader } from '@/widgets/smart-header'
import { Footer } from '@/widgets/footer'
import { CellForm } from '@/widgets/cell-form'
import { motion } from 'framer-motion'
import {
  DollarSign,
  Zap,
  FileCheck,
  Shield,
  TrendingUp,
  Receipt,
  FileSpreadsheet,
  Building2
} from 'lucide-react'

// Metadata moved to layout or separate metadata file

export default function SellOOOPage() {
  return (
    <div className="relative flex min-h-screen flex-col">
      <SmartHeader />
      <main className="flex-1 pt-14">
        {/* Hero секция */}
        <section className="container pt-12 md:pt-20 pb-6 md:pb-8">
          <div className="mx-auto max-w-4xl text-center">
            <h1 className="mb-6 text-3xl md:text-5xl font-bold">Продать ООО</h1>
            <p className="mb-8 text-xl text-muted-foreground">
              Выгодная альтернатива ликвидации. Продайте свое ООО быстро и безопасно с полным
              юридическим сопровождением.
            </p>

            {/* Ключевые преимущества */}
            <div className="flex flex-wrap justify-center gap-4 md:gap-6 text-sm md:text-base text-muted-foreground">
              <div className="flex items-center gap-2">
                <span className="text-orange-500 text-lg">✓</span>
                <span>Быстрая продажа</span>
              </div>
              <div className="flex items-center gap-2">
                <span className="flex items-center gap-2">
                  <span className="text-orange-500 text-lg">✓</span>
                  <span>Полная оплата</span>
                </span>
              </div>
              <div className="flex items-center gap-2">
                <span className="text-orange-500 text-lg">✓</span>
                <span>Юридическое сопровождение</span>
              </div>
            </div>
          </div>
        </section>

        {/* Форма заявки */}
        <section className="container py-3 bg-muted/20">
          <div className="mx-auto max-w-2xl">
            <CellForm />
          </div>
        </section>

        {/* Преимущества продажи */}
        <section className="container py-6 md:py-12 bg-muted/20">
          <div className="mx-auto max-w-6xl">
            <motion.h2
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5 }}
              className="text-2xl md:text-3xl font-bold text-center mb-4 md:mb-6"
            >
              Почему продажа лучше ликвидации
            </motion.h2>

            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 md:gap-8">
              <motion.div
                initial={{ opacity: 0, scale: 0.95 }}
                whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5 }}
                className="flex items-start gap-3 md:gap-4 p-4 md:p-6 rounded-lg border hover:border-orange-500 transition-colors bg-background"
              >
                <div className="w-12 h-12 md:w-16 md:h-16 rounded-full bg-orange-100 dark:bg-orange-900/20 flex items-center justify-center flex-shrink-0">
                  <DollarSign className="w-6 h-6 md:w-8 md:h-8 text-orange-500" />
                </div>
                <div>
                  <h3 className="text-lg md:text-xl font-semibold mb-2">Получите оплату</h3>
                  <p className="text-sm md:text-base text-muted-foreground">
                    Вместо трат на ликвидацию получите деньги за свое ООО
                  </p>
                </div>
              </motion.div>

              <motion.div
                initial={{ opacity: 0, scale: 0.95 }}
                whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: 0.1 }}
                className="flex items-start gap-3 md:gap-4 p-4 md:p-6 rounded-lg border hover:border-orange-500 transition-colors bg-background"
              >
                <div className="w-12 h-12 md:w-16 md:h-16 rounded-full bg-orange-100 dark:bg-orange-900/20 flex items-center justify-center flex-shrink-0">
                  <Zap className="w-6 h-6 md:w-8 md:h-8 text-orange-500" />
                </div>
                <div>
                  <h3 className="text-lg md:text-xl font-semibold mb-2">Быстро</h3>
                  <p className="text-sm md:text-base text-muted-foreground">
                    Продажа занимает 3-5 дней против 6+ месяцев ликвидации
                  </p>
                </div>
              </motion.div>

              <motion.div
                initial={{ opacity: 0, scale: 0.95 }}
                whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: 0.2 }}
                className="flex items-start gap-3 md:gap-4 p-4 md:p-6 rounded-lg border hover:border-orange-500 transition-colors bg-background"
              >
                <div className="w-12 h-12 md:w-16 md:h-16 rounded-full bg-orange-100 dark:bg-orange-900/20 flex items-center justify-center flex-shrink-0">
                  <FileCheck className="w-6 h-6 md:w-8 md:h-8 text-orange-500" />
                </div>
                <div>
                  <h3 className="text-lg md:text-xl font-semibold mb-2">Без бумажной работы</h3>
                  <p className="text-sm md:text-base text-muted-foreground">
                    Минимум документооборота, мы берем все на себя
                  </p>
                </div>
              </motion.div>

              <motion.div
                initial={{ opacity: 0, scale: 0.95 }}
                whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: 0.3 }}
                className="flex items-start gap-3 md:gap-4 p-4 md:p-6 rounded-lg border hover:border-orange-500 transition-colors bg-background"
              >
                <div className="w-12 h-12 md:w-16 md:h-16 rounded-full bg-orange-100 dark:bg-orange-900/20 flex items-center justify-center flex-shrink-0">
                  <Shield className="w-6 h-6 md:w-8 md:h-8 text-orange-500" />
                </div>
                <div>
                  <h3 className="text-lg md:text-xl font-semibold mb-2">Безопасно</h3>
                  <p className="text-sm md:text-base text-muted-foreground">
                    Полное юридическое сопровождение, защита от рисков
                  </p>
                </div>
              </motion.div>
            </div>
          </div>
        </section>

        {/* Какие ООО покупаем */}
        <section className="container py-12 bg-muted/20">
          <div className="mx-auto max-w-4xl">
            <h2 className="mb-12 text-2xl md:text-3xl font-bold text-center">
              Какие ООО мы покупаем
            </h2>

            <div className="grid md:grid-cols-2 gap-6 md:gap-8">
              <motion.div
                initial={{ opacity: 0, scale: 0.95 }}
                whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5 }}
                className="p-6 md:p-8 rounded-lg border hover:border-orange-500 transition-colors bg-background"
              >
                <div className="w-12 h-12 md:w-16 md:h-16 rounded-full bg-green-100 dark:bg-green-900/20 flex items-center justify-center mx-auto mb-6">
                  <FileCheck className="w-6 h-6 md:w-8 md:h-8 text-green-500" />
                </div>
                <h3 className="text-xl font-semibold text-green-600 mb-6 text-center">
                  ✅ Покупаем
                </h3>
                <ul className="space-y-3">
                  <li className="flex items-center gap-3">
                    <div className="w-6 h-6 rounded-full bg-green-100 dark:bg-green-900/20 flex items-center justify-center flex-shrink-0">
                      <span className="text-green-500 text-sm">✓</span>
                    </div>
                    <span>ООО без долгов и обязательств</span>
                  </li>
                  <li className="flex items-center gap-3">
                    <div className="w-6 h-6 rounded-full bg-green-100 dark:bg-green-900/20 flex items-center justify-center flex-shrink-0">
                      <span className="text-green-500 text-sm">✓</span>
                    </div>
                    <span>С чистой налоговой и судебной историей</span>
                  </li>
                  <li className="flex items-center gap-3">
                    <div className="w-6 h-6 rounded-full bg-green-100 dark:bg-green-900/20 flex items-center justify-center flex-shrink-0">
                      <span className="text-green-500 text-sm">✓</span>
                    </div>
                    <span>С оборотами и без оборотов</span>
                  </li>
                  <li className="flex items-center gap-3">
                    <div className="w-6 h-6 rounded-full bg-green-100 dark:bg-green-900/20 flex items-center justify-center flex-shrink-0">
                      <span className="text-green-500 text-sm">✓</span>
                    </div>
                    <span>С банковскими счетами</span>
                  </li>
                  <li className="flex items-center gap-3">
                    <div className="w-6 h-6 rounded-full bg-green-100 dark:bg-green-900/20 flex items-center justify-center flex-shrink-0">
                      <span className="text-green-500 text-sm">✓</span>
                    </div>
                    <span>С лицензиями и СРО допусками</span>
                  </li>
                  <li className="flex items-center gap-3">
                    <div className="w-6 h-6 rounded-full bg-green-100 dark:bg-green-900/20 flex items-center justify-center flex-shrink-0">
                      <span className="text-green-500 text-sm">✓</span>
                    </div>
                    <span>С историей от 1 года</span>
                  </li>
                </ul>
              </motion.div>

              <motion.div
                initial={{ opacity: 0, scale: 0.95 }}
                whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: 0.1 }}
                className="p-6 md:p-8 rounded-lg border hover:border-red-500 transition-colors bg-background"
              >
                <div className="w-12 h-12 md:w-16 md:h-16 rounded-full bg-red-100 dark:bg-red-900/20 flex items-center justify-center mx-auto mb-6">
                  <Shield className="w-6 h-6 md:w-8 md:h-8 text-red-500" />
                </div>
                <h3 className="text-xl font-semibold text-red-600 mb-6 text-center">
                  ❌ Не покупаем
                </h3>
                <ul className="space-y-3">
                  <li className="flex items-center gap-3">
                    <div className="w-6 h-6 rounded-full bg-red-100 dark:bg-red-900/20 flex items-center justify-center flex-shrink-0">
                      <span className="text-red-500 text-sm">✗</span>
                    </div>
                    <span>ООО с долгами перед бюджетом</span>
                  </li>
                  <li className="flex items-center gap-3">
                    <div className="w-6 h-6 rounded-full bg-red-100 dark:bg-red-900/20 flex items-center justify-center flex-shrink-0">
                      <span className="text-red-500 text-sm">✗</span>
                    </div>
                    <span>С судебными разбирательствами</span>
                  </li>
                  <li className="flex items-center gap-3">
                    <div className="w-6 h-6 rounded-full bg-red-100 dark:bg-red-900/20 flex items-center justify-center flex-shrink-0">
                      <span className="text-red-500 text-sm">✗</span>
                    </div>
                    <span>С заблокированными счетами</span>
                  </li>
                  <li className="flex items-center gap-3">
                    <div className="w-6 h-6 rounded-full bg-red-100 dark:bg-red-900/20 flex items-center justify-center flex-shrink-0">
                      <span className="text-red-500 text-sm">✗</span>
                    </div>
                    <span>В процессе банкротства</span>
                  </li>
                  <li className="flex items-center gap-3">
                    <div className="w-6 h-6 rounded-full bg-red-100 dark:bg-red-900/20 flex items-center justify-center flex-shrink-0">
                      <span className="text-red-500 text-sm">✗</span>
                    </div>
                    <span>С приостановленной деятельностью</span>
                  </li>
                  <li className="flex items-center gap-3">
                    <div className="w-6 h-6 rounded-full bg-red-100 dark:bg-red-900/20 flex items-center justify-center flex-shrink-0">
                      <span className="text-red-500 text-sm">✗</span>
                    </div>
                    <span>Возрастом менее 6 месяцев</span>
                  </li>
                </ul>
              </motion.div>
            </div>
          </div>
        </section>

        {/* Процесс продажи */}
        <section className="container py-12 bg-muted/20">
          <div className="mx-auto max-w-4xl">
            <h2 className="mb-12 text-2xl md:text-3xl font-bold text-center">Как продать ООО</h2>

            <div className="grid md:grid-cols-2 gap-6 md:gap-8">
              <motion.div
                initial={{ opacity: 0, scale: 0.95 }}
                whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5 }}
                className="flex items-start gap-3 md:gap-4 p-4 md:p-6 rounded-lg border hover:border-orange-500 transition-colors bg-background"
              >
                <div className="w-12 h-12 bg-orange-500 text-white rounded-full flex items-center justify-center flex-shrink-0 font-bold">
                  1
                </div>
                <div>
                  <h3 className="text-lg md:text-xl font-semibold mb-2">Заявка и оценка</h3>
                  <p className="text-sm md:text-base text-muted-foreground">
                    Оставьте заявку с информацией о вашем ООО. Проведем предварительную оценку и
                    сообщим возможную стоимость.
                  </p>
                </div>
              </motion.div>

              <motion.div
                initial={{ opacity: 0, scale: 0.95 }}
                whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: 0.1 }}
                className="flex items-start gap-3 md:gap-4 p-4 md:p-6 rounded-lg border hover:border-orange-500 transition-colors bg-background"
              >
                <div className="w-12 h-12 bg-orange-500 text-white rounded-full flex items-center justify-center flex-shrink-0 font-bold">
                  2
                </div>
                <div>
                  <h3 className="text-lg md:text-xl font-semibold mb-2">Проверка документов</h3>
                  <p className="text-sm md:text-base text-muted-foreground">
                    Анализ документов, налоговой и судебной истории ООО.
                  </p>
                </div>
              </motion.div>

              <motion.div
                initial={{ opacity: 0, scale: 0.95 }}
                whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: 0.2 }}
                className="flex items-start gap-3 md:gap-4 p-4 md:p-6 rounded-lg border hover:border-orange-500 transition-colors bg-background"
              >
                <div className="w-12 h-12 bg-orange-500 text-white rounded-full flex items-center justify-center flex-shrink-0 font-bold">
                  3
                </div>
                <div>
                  <h3 className="text-lg md:text-xl font-semibold mb-2">Согласование условий</h3>
                  <p className="text-sm md:text-base text-muted-foreground">
                    Определяем окончательную стоимость, согласовываем условия сделки и сроки
                    передачи документов.
                  </p>
                </div>
              </motion.div>

              <motion.div
                initial={{ opacity: 0, scale: 0.95 }}
                whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: 0.3 }}
                className="flex items-start gap-3 md:gap-4 p-4 md:p-6 rounded-lg border hover:border-orange-500 transition-colors bg-background"
              >
                <div className="w-12 h-12 bg-orange-500 text-white rounded-full flex items-center justify-center flex-shrink-0 font-bold">
                  4
                </div>
                <div>
                  <h3 className="text-lg md:text-xl font-semibold mb-2">Оформление сделки</h3>
                  <p className="text-sm md:text-base text-muted-foreground">
                    Получение оплаты, подписание договора, передача документов, регистрация смены
                    собственника.
                  </p>
                </div>
              </motion.div>
            </div>
          </div>
        </section>

        {/* Стоимость */}
        <section className="container py-12 bg-muted/20">
          <div className="mx-auto max-w-4xl text-center">
            <h2 className="mb-8 text-2xl md:text-3xl font-bold">От чего зависит стоимость ООО</h2>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-8">
              <motion.div
                initial={{ opacity: 0, scale: 0.95 }}
                whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5 }}
                className="flex flex-col items-center text-center p-4 md:p-6 rounded-lg border hover:border-orange-500 transition-colors bg-background"
              >
                <div className="w-12 h-12 md:w-16 md:h-16 rounded-full bg-orange-100 dark:bg-orange-900/20 flex items-center justify-center mb-3 md:mb-4">
                  <Receipt className="w-6 h-6 md:w-8 md:h-8 text-orange-500" />
                </div>
                <h3 className="text-lg md:text-xl font-semibold mb-2">Налогообложение</h3>
                <p className="text-sm md:text-base text-muted-foreground">ОСНО с НДС дороже УСН</p>
              </motion.div>

              <motion.div
                initial={{ opacity: 0, scale: 0.95 }}
                whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: 0.1 }}
                className="flex flex-col items-center text-center p-4 md:p-6 rounded-lg border hover:border-orange-500 transition-colors bg-background"
              >
                <div className="w-12 h-12 md:w-16 md:h-16 rounded-full bg-orange-100 dark:bg-orange-900/20 flex items-center justify-center mb-3 md:mb-4">
                  <TrendingUp className="w-6 h-6 md:w-8 md:h-8 text-orange-500" />
                </div>
                <h3 className="text-lg md:text-xl font-semibold mb-2">Обороты</h3>
                <p className="text-sm md:text-base text-muted-foreground">
                  ООО с оборотами стоят дороже
                </p>
              </motion.div>

              <motion.div
                initial={{ opacity: 0, scale: 0.95 }}
                whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: 0.2 }}
                className="flex flex-col items-center text-center p-4 md:p-6 rounded-lg border hover:border-orange-500 transition-colors bg-background"
              >
                <div className="w-12 h-12 md:w-16 md:h-16 rounded-full bg-orange-100 dark:bg-orange-900/20 flex items-center justify-center mb-3 md:mb-4">
                  <FileSpreadsheet className="w-6 h-6 md:w-8 md:h-8 text-orange-500" />
                </div>
                <h3 className="text-lg md:text-xl font-semibold mb-2">Лицензии</h3>
                <p className="text-sm md:text-base text-muted-foreground">
                  Наличие лицензий увеличивает стоимость
                </p>
              </motion.div>

              <motion.div
                initial={{ opacity: 0, scale: 0.95 }}
                whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: 0.3 }}
                className="flex flex-col items-center text-center p-4 md:p-6 rounded-lg border hover:border-orange-500 transition-colors bg-background"
              >
                <div className="w-12 h-12 md:w-16 md:h-16 rounded-full bg-orange-100 dark:bg-orange-900/20 flex items-center justify-center mb-3 md:mb-4">
                  <Building2 className="w-6 h-6 md:w-8 md:h-8 text-orange-500" />
                </div>
                <h3 className="text-lg md:text-xl font-semibold mb-2">Возраст</h3>
                <p className="text-sm md:text-base text-muted-foreground">Старше ООО — выше цена</p>
              </motion.div>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  )
}
